const USERINFO_STORAGE_KEY = 'userinfo';

/** Company admin dashboard (dashboardcontainer → CompanyadmindashboardNew). */
const COMPANY_ADMIN_USER_TYPES = ['usertype02', 'usertype06'];

/** Vendor dashboard (dashboardcontainer → venderuserdashboard). */
const VENDOR_USER_TYPES = ['usertype11', 'usertype12'];

/** Company user / co-owner dashboard (dashboardcontainer → companyUserDashboard). */
export const COMPANY_USER_DASHBOARD_USER_TYPES = [
    'usertype03',
    'usertype05',
    'usertype07',
    'usertype08',
    'usertype21'
];

/**
 * Company user dashboard Communication (account key = lowercase userName or email).
 * Includes psing@ alias for psingh@.
 */
export const COMPANY_USER_COMMUNICATION_BY_ACCOUNT = {
    'psingh@yopmail.com':
        'https://ucaas.talygen.com/Communication/Index?key=Cvpe7Va1VkJHsXSnP/FEdT+TPzt6luosQkzkdL0QnRw=',
    'psing@yopmail.com':
        'https://ucaas.talygen.com/Communication/Index?key=Cvpe7Va1VkJHsXSnP/FEdT+TPzt6luosQkzkdL0QnRw=',
    'kghuman@yopmail.com':
        'https://ucaas.talygen.com/Communication/Index?key=4rADURPXUcN/GnQbK4WFIGyAxpo5YNkz1uU69++po2I=',
    'schamoli@yopmail.com':
        'https://ucaas.talygen.com/Communication/Index?key=2F3NLskf3qLu3qSKtd2/c/d1aGVamSx3Me7x5GdIwsY=',
    'hsingh@yopmail.com':
        'https://ucaas.talygen.com/Communication/Index?key=tDvvK3l24RLMog/ZIZZUFll4TxgVNKyT6Wz0/LX45Ew=',
    'gkennedy@yopmail.com':
        'https://ucaas.talygen.com/Communication/Index?key=gS8kNrqfuqbmaMtZ1K2/eOj9DFIlGZ0QqEuQTNi4N68=',
    'harris@yopmail.com':
        'https://ucaas.talygen.com/Communication/Index?key=XyTv3o876tYwyjY3U9dCcC289d6ZclORuZKwpYQcE+Q='
};

/**
 * Communication portal access: email + allowed user types + UCaaS URL.
 * Co-owners (usertype05) and other types are intentionally excluded.
 */
export const COMMUNICATION_PORTAL_ACCESS = [
    {
        email: 'alexk@yopmail.com',
        userTypes: COMPANY_ADMIN_USER_TYPES,
        url: 'https://ucaas.talygen.com/Communication/Index?key=d8fOoT0iPsuzvEciTQL+I9SbMZTHCM7B7lQco03blQg='
    },
    {
        email: 'psingh@yopmail.com',
        userTypes: VENDOR_USER_TYPES,
        url: 'https://ucaas.talygen.com/Communication/Index?key=Cvpe7Va1VkJHsXSnP/FEdT+TPzt6luosQkzkdL0QnRw='
    }
];

/** @deprecated Use COMMUNICATION_PORTAL_ACCESS — kept for existing imports */
export const COMMUNICATION_PORTAL_BY_EMAIL = Object.fromEntries(
    COMMUNICATION_PORTAL_ACCESS.map((entry) => [entry.email, entry.url])
);

/** @deprecated Use COMMUNICATION_PORTAL_ACCESS — kept for existing imports */
export const COMMUNICATION_DASHBOARD_EMAIL = 'alexk@yopmail.com';

/** @deprecated Use getCommunicationPortalUrl() — kept for existing imports */
export const COMMUNICATION_PORTAL_URL = COMMUNICATION_PORTAL_BY_EMAIL[COMMUNICATION_DASHBOARD_EMAIL];

function normalizeUserType(value) {
    return String(value || '').trim().toLowerCase();
}

function normalizeAccountKey(value) {
    return String(value || '').trim().toLowerCase();
}

/**
 * Login account key from userinfo (userName or email, case-insensitive).
 */
export function getLoggedInAccountKey() {
    const data = getLoggedInUserInfoData();
    if (!data) {
        return '';
    }
    const fields = ['userName', 'UserName', 'email', 'Email'];
    for (let i = 0; i < fields.length; i++) {
        const key = normalizeAccountKey(data[fields[i]]);
        if (key) {
            return key;
        }
    }
    return '';
}

/**
 * UCaaS URL for company user dashboard when account is in allow list.
 */
export function getCompanyUserCommunicationUrl(accountKey) {
    const key =
        accountKey != null && accountKey !== '' ? normalizeAccountKey(accountKey) : getLoggedInAccountKey();
    if (!key) {
        return null;
    }
    return COMPANY_USER_COMMUNICATION_BY_ACCOUNT[key] ?? null;
}

export function isCompanyUserCommunicationEnabled(accountKey, userType) {
    const url = getCompanyUserCommunicationUrl(accountKey);
    if (!url) {
        return false;
    }
    const resolvedType =
        userType != null && userType !== '' ? normalizeUserType(userType) : getLoggedInUserType();
    return COMPANY_USER_DASHBOARD_USER_TYPES.includes(resolvedType);
}

export function openCompanyUserCommunication(accountKey) {
    const url = getCompanyUserCommunicationUrl(accountKey);
    if (!url) {
        return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Returns parsed userinfo.data from localStorage, or null if missing/invalid.
 */
export function getLoggedInUserInfoData() {
    try {
        const raw = localStorage.getItem(USERINFO_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        const data = parsed?.data;
        return data && typeof data === 'object' ? data : null;
    } catch {
        return null;
    }
}

/**
 * Reads logged-in user type from localStorage / userinfo.
 */
export function getLoggedInUserType() {
    try {
        const fromStorage = localStorage.getItem('userType');
        if (fromStorage) {
            return normalizeUserType(fromStorage);
        }
        const data = getLoggedInUserInfoData();
        return normalizeUserType(data?.userType ?? data?.UserType);
    } catch {
        return '';
    }
}

/**
 * Reads account email from userinfo (email field only; userName only if it looks like an email).
 */
export function getLoggedInUserEmail() {
    const data = getLoggedInUserInfoData();
    if (!data) {
        return '';
    }
    const direct = data.email ?? data.Email;
    if (direct != null && String(direct).trim() !== '') {
        return String(direct).trim();
    }
    const userName = data.userName ?? data.UserName ?? '';
    const asText = String(userName).trim();
    return asText.includes('@') ? asText : '';
}

/**
 * Returns Communication access config when email and user type both match, else null.
 */
export function getCommunicationPortalConfig(email, userType) {
    const resolvedEmail = email != null && email !== '' ? String(email).trim() : getLoggedInUserEmail();
    const resolvedType =
        userType != null && userType !== '' ? normalizeUserType(userType) : getLoggedInUserType();

    if (!resolvedEmail || !resolvedType) {
        return null;
    }

    return (
        COMMUNICATION_PORTAL_ACCESS.find(
            (entry) =>
                entry.email === resolvedEmail &&
                entry.userTypes.map(normalizeUserType).includes(resolvedType)
        ) ?? null
    );
}

/**
 * Returns the Communication portal URL for the given or current user, or null.
 */
export function getCommunicationPortalUrl(email, userType) {
    return getCommunicationPortalConfig(email, userType)?.url ?? null;
}

export function isCommunicationDashboardEnabled(email, userType) {
    return getCommunicationPortalConfig(email, userType) != null;
}

export function openCommunicationPortal(email, userType) {
    const url = getCommunicationPortalUrl(email, userType);
    if (!url) {
        return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
}
